import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Search, Download, Users, BarChart3, User, ChevronDown, ChevronUp, Lock, Eye, MessageSquare, MapPin, Filter, RefreshCw } from 'lucide-react';
import './HostDashboard.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ======================================================================
   LOGIN
   ====================================================================== */
function HostLogin({ onLogin }) {
  const [host, setHost] = useState('richard');
  const [key, setKey] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API}/host/people?status=completed`, { headers: { 'x-host-key': key } });
      if (!res.ok) throw new Error('Invalid credentials');
      onLogin(key, host);
    } catch {
      setErr('Access denied. Check your host key.');
    }
    setLoading(false);
  };

  return (
    <div className="host-login" data-testid="host-login">
      <div className="host-login-card">
        <Lock size={28} className="host-login-icon" />
        <h1>Host Dashboard</h1>
        <p className="host-login-sub">For pilot hosts only. Enter your credentials to continue.</p>
        <div className="host-field">
          <label>Host</label>
          <select value={host} onChange={e => setHost(e.target.value)} data-testid="host-select">
            <option value="richard">Richard</option>
            <option value="louise">Louise</option>
          </select>
        </div>
        <div className="host-field">
          <label>Host Key</label>
          <input type="password" value={key} onChange={e => setKey(e.target.value)}
            placeholder="spring-host-..." data-testid="host-key-input"
            onKeyDown={e => e.key === 'Enter' && submit()} />
        </div>
        {err && <p className="host-err" data-testid="host-login-error">{err}</p>}
        <button className="host-btn-primary" onClick={submit} disabled={loading || !key}
          data-testid="host-login-btn">
          {loading ? 'Checking...' : 'Enter Dashboard'}
          {!loading && <ArrowRight size={16} />}
        </button>
      </div>
    </div>
  );
}

/* ======================================================================
   PEOPLE LIST
   ====================================================================== */
function PeopleView({ hostKey, onSelectPerson }) {
  const [people, setPeople] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pathwayFilter, setPathwayFilter] = useState('');
  const [sortBy, setSortBy] = useState('completed_at');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (pathwayFilter) params.set('pathway', pathwayFilter);
    params.set('sort_by', sortBy);
    params.set('sort_dir', sortDir);
    try {
      const res = await fetch(`${API}/host/people?${params}`, { headers: { 'x-host-key': hostKey } });
      const data = await res.json();
      setPeople(data.people);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [hostKey, search, statusFilter, pathwayFilter, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    const res = await fetch(`${API}/host/people/csv`, { headers: { 'x-host-key': hostKey } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'spring_people.csv'; a.click();
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const pathwayLabel = (p) => {
    const map = { pathway_a: 'Founders', pathway_b: 'Employment', pathway_c: 'Independent' };
    return map[p] || p || '-';
  };

  const pathwayClass = (p) => {
    const map = { pathway_a: 'pw-a', pathway_b: 'pw-b', pathway_c: 'pw-c' };
    return map[p] || '';
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div data-testid="host-people-view">
      <div className="host-toolbar">
        <div className="host-search-wrap">
          <Search size={16} />
          <input placeholder="Search name or email..." value={search}
            onChange={e => setSearch(e.target.value)} data-testid="host-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="host-status-filter">
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="draft">In progress</option>
        </select>
        <select value={pathwayFilter} onChange={e => setPathwayFilter(e.target.value)} data-testid="host-pathway-filter">
          <option value="">All pathways</option>
          <option value="pathway_a">Founders</option>
          <option value="pathway_b">Employment</option>
          <option value="pathway_c">Independent</option>
        </select>
        <button className="host-btn-outline" onClick={exportCSV} data-testid="host-csv-btn">
          <Download size={14} /> CSV
        </button>
        <button className="host-btn-outline" onClick={load} data-testid="host-refresh-btn">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="host-count">{total} people</div>

      {loading ? (
        <div className="host-loading">Loading...</div>
      ) : (
        <div className="host-table-wrap">
          <table className="host-table" data-testid="host-people-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('email')} className="host-sortable">
                  Name / Email {sortBy === 'email' && (sortDir === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>)}
                </th>
                <th>Status</th>
                <th>Pathway</th>
                <th onClick={() => toggleSort('started_at')} className="host-sortable">
                  Started {sortBy === 'started_at' && (sortDir === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>)}
                </th>
                <th onClick={() => toggleSort('completed_at')} className="host-sortable">
                  Completed {sortBy === 'completed_at' && (sortDir === 'asc' ? <ChevronUp size={12}/> : <ChevronDown size={12}/>)}
                </th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {people.map(p => (
                <tr key={p.attempt_id} className="host-row" onClick={() => onSelectPerson(p.attempt_id)}
                  data-testid={`host-person-row-${p.attempt_id}`}>
                  <td>
                    <div className="host-name-cell">
                      <span className="host-name">{p.name || '(no name)'}</span>
                      <span className="host-email">{p.email}</span>
                    </div>
                  </td>
                  <td><span className={`host-status-badge ${p.status}`}>{p.status}</span></td>
                  <td>
                    {p.suggested_pathway && (
                      <span className={`host-pw-badge ${pathwayClass(p.suggested_pathway)}`}>
                        {pathwayLabel(p.suggested_pathway)}
                      </span>
                    )}
                  </td>
                  <td className="host-date">{formatDate(p.started_at)}</td>
                  <td className="host-date">{formatDate(p.completed_at)}</td>
                  <td className="host-flags">
                    {p.is_close_call && <span className="host-flag cc" title="Close call">CC</span>}
                    {p.is_insufficient && <span className="host-flag ins" title="Insufficient">INS</span>}
                    {p.feedback_given && <span className="host-flag fb" title="Feedback given">FB</span>}
                    {p.satisfaction && <span className="host-flag sat" title={`Satisfaction: ${p.satisfaction}`}>{p.satisfaction === 'Yes' ? 'Y' : p.satisfaction === 'Mostly' ? 'M' : 'N'}</span>}
                  </td>
                </tr>
              ))}
              {people.length === 0 && (
                <tr><td colSpan={6} className="host-empty">No people found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ======================================================================
   PERSON DETAIL — 4 TABS
   ====================================================================== */
function PersonView({ hostKey, attemptId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('report');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/host/person/${attemptId}`, { headers: { 'x-host-key': hostKey } });
        setData(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [hostKey, attemptId]);

  if (loading) return <div className="host-loading">Loading person...</div>;
  if (!data) return <div className="host-loading">Person not found</div>;

  const pathwayLabel = (p) => {
    const map = { pathway_a: 'Founders (A)', pathway_b: 'Employment (B)', pathway_c: 'Independent (C)' };
    return map[p] || p || '-';
  };

  return (
    <div data-testid="host-person-view">
      <button className="host-btn-back" onClick={onBack} data-testid="host-back-btn">
        <ArrowLeft size={16} /> Back to People
      </button>

      <div className="host-person-header">
        <h2>{data.name || data.email}</h2>
        <div className="host-person-meta">
          <span>{data.email}</span>
          <span>Attempt #{data.attempt_number}</span>
          <span className={`host-status-badge ${data.status}`}>{data.status}</span>
          {data.scoring?.suggested_pathway && (
            <span className="host-pw-badge">{pathwayLabel(data.scoring.suggested_pathway)}</span>
          )}
        </div>
      </div>

      <div className="host-tabs" data-testid="host-person-tabs">
        {['report', 'answers', 'feedback', 'journey'].map(t => (
          <button key={t} className={`host-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)} data-testid={`host-tab-${t}`}>
            {t === 'report' && <Eye size={14} />}
            {t === 'answers' && <MessageSquare size={14} />}
            {t === 'feedback' && <BarChart3 size={14} />}
            {t === 'journey' && <MapPin size={14} />}
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="host-tab-content">
        {tab === 'report' && <ReportTab data={data} />}
        {tab === 'answers' && <AnswersTab data={data} />}
        {tab === 'feedback' && <FeedbackTab data={data} />}
        {tab === 'journey' && <JourneyTab data={data} />}
      </div>
    </div>
  );
}

function ReportTab({ data }) {
  const r = data.report || {};
  const s = data.scoring || {};
  return (
    <div className="host-report" data-testid="host-report-tab">
      <div className="host-section-card">
        <h3>Scoring Summary</h3>
        <div className="host-kv-grid">
          <KV label="Suggested Pathway" value={s.suggested_pathway || '-'} />
          <KV label="Orientation" value={s.orientation || '-'} />
          <KV label="Step 2" value={s.step2 || '-'} />
          <KV label="Drive Strength" value={s.drive_strength || '-'} />
          <KV label="Durability" value={s.durability_read || '-'} />
          <KV label="Skills Rating" value={s.skills_strength || '-'} />
          <KV label="Values" value={s.values_agreement || '-'} />
          <KV label="Close Call" value={s.is_close_call ? 'Yes' : 'No'} />
          <KV label="Insufficient" value={s.is_insufficient ? 'Yes' : 'No'} />
          <KV label="Confidence" value={s.confidence || '-'} />
        </div>
      </div>

      {r.headline && (
        <div className="host-section-card">
          <h3>Report Headline</h3>
          <p className="host-report-headline">{r.headline}</p>
        </div>
      )}

      {r.reasoning_block && (
        <div className="host-section-card">
          <h3>Reasoning</h3>
          {r.reasoning_block.split('\n\n').map((p, i) => <p key={i} className="host-report-para">{p}</p>)}
        </div>
      )}

      {r.tension_note && (
        <div className="host-section-card host-tension">
          <h3>Tension Note</h3>
          <p>{r.tension_note}</p>
        </div>
      )}

      {r.strengths_summary && (
        <div className="host-section-card">
          <h3>Strengths Summary</h3>
          <p>{r.strengths_summary}</p>
        </div>
      )}

      {r.other_paths?.length > 0 && (
        <div className="host-section-card">
          <h3>Other Paths Considered</h3>
          {r.other_paths.map((op, i) => (
            <div key={i} className="host-other-path">
              <h4>{op.heading || op.pathway}</h4>
              <p>{op.reasoning}</p>
            </div>
          ))}
        </div>
      )}

      <div className="host-section-card">
        <h3>Financial Section</h3>
        <p className="host-privacy-note">Financial amounts are not shown. Only completion status is displayed.</p>
        <div className="host-fin-checklist">
          {Object.entries(data.financial_checklist || {}).map(([k, v]) => (
            <div key={k} className={`host-fin-item ${v}`}>
              <span className="host-fin-label">{k.replace(/_/g, ' ')}</span>
              <span className={`host-fin-status ${v}`}>{v === 'completed' ? 'Completed' : 'Not started'}</span>
            </div>
          ))}
        </div>
      </div>

      {data.satisfaction && (
        <div className="host-section-card">
          <h3>Satisfaction Check-in</h3>
          <KV label="Accuracy" value={data.satisfaction.accuracy || '-'} />
          {data.satisfaction.comment && <p className="host-sat-comment">"{data.satisfaction.comment}"</p>}
        </div>
      )}
    </div>
  );
}

function AnswersTab({ data }) {
  const answers = data.answers || {};
  const keys = Object.keys(answers).sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, '')) || 0;
    const nb = parseInt(b.replace(/\D/g, '')) || 0;
    return na - nb;
  });

  const renderValue = (val) => {
    if (val === null || val === undefined) return <span className="host-muted">-</span>;
    if (typeof val === 'string') return <span>{val}</span>;
    if (typeof val === 'number') return <span>{val}</span>;
    if (typeof val === 'boolean') return <span>{val ? 'Yes' : 'No'}</span>;
    if (Array.isArray(val)) return <span>{val.join(', ')}</span>;
    if (typeof val === 'object') {
      return (
        <div className="host-answer-obj">
          {Object.entries(val).map(([k, v]) => (
            <div key={k} className="host-answer-kv">
              <span className="host-answer-key">{k}:</span>
              <span className="host-answer-val">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
            </div>
          ))}
        </div>
      );
    }
    return <span>{String(val)}</span>;
  };

  return (
    <div className="host-answers" data-testid="host-answers-tab">
      <p className="host-privacy-note">Section 3 financial amounts are hidden. Only the risk posture question (Q29) is shown.</p>
      {keys.map(qid => (
        <div key={qid} className="host-answer-row">
          <span className="host-qid">{qid}</span>
          <div className="host-answer-value">{renderValue(answers[qid])}</div>
        </div>
      ))}
      {keys.length === 0 && <p className="host-empty-msg">No answers recorded yet.</p>}
    </div>
  );
}

function FeedbackTab({ data }) {
  const feedback = data.feedback || [];
  const fbModule = data.fb_module || [];
  const all = [...feedback, ...fbModule];

  return (
    <div className="host-feedback" data-testid="host-feedback-tab">
      {all.length === 0 ? (
        <p className="host-empty-msg">No feedback submitted yet.</p>
      ) : (
        all.map((fb, i) => (
          <div key={i} className="host-feedback-item">
            <div className="host-feedback-q">{fb.question_id || fb.checkpoint || `Item ${i + 1}`}</div>
            {fb.rating_value !== undefined && <div className="host-feedback-rating">Rating: {fb.rating_value}</div>}
            {fb.text_value && <div className="host-feedback-text">"{fb.text_value}"</div>}
            {fb.response_type && <span className="host-feedback-type">{fb.response_type}</span>}
          </div>
        ))
      )}
    </div>
  );
}

function JourneyTab({ data }) {
  const events = data.journey || [];
  const formatTime = (t) => {
    if (!t) return '';
    try { return new Date(t).toLocaleString('en-NZ', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return t; }
  };

  return (
    <div className="host-journey" data-testid="host-journey-tab">
      <div className="host-journey-meta">
        <KV label="Started" value={formatTime(data.started_at)} />
        <KV label="Completed" value={formatTime(data.completed_at)} />
      </div>
      {events.length === 0 ? (
        <p className="host-empty-msg">No journey events recorded.</p>
      ) : (
        <div className="host-timeline">
          {events.map((e, i) => (
            <div key={i} className="host-timeline-item">
              <div className="host-timeline-dot" />
              <div className="host-timeline-content">
                <span className="host-timeline-screen">{e.screen}</span>
                <span className="host-timeline-event">{e.event}</span>
                <span className="host-timeline-time">{formatTime(e.time || e.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ======================================================================
   PATTERNS
   ====================================================================== */
function PatternsView({ hostKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/host/patterns`, { headers: { 'x-host-key': hostKey } });
        setData(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [hostKey]);

  if (loading) return <div className="host-loading">Loading patterns...</div>;
  if (!data) return <div className="host-loading">No data</div>;

  const f = data.funnel || {};
  const o = data.outcomes || {};
  const t = data.time || {};
  const fin = data.finance_section || {};
  const sat = data.satisfaction || {};

  const funnelSteps = [
    ['Entered email', f.entered_email],
    ['Completed Sec 1', f.completed_section_1],
    ['Completed Sec 2', f.completed_section_2],
    ['Completed Sec 3', f.completed_section_3],
    ['Completed Sec 4', f.completed_section_4],
    ['Submitted', f.submitted],
    ['Viewed report', f.viewed_report],
    ['Gave feedback', f.completed_feedback],
  ];

  const maxFunnel = Math.max(...funnelSteps.map(s => s[1] || 0), 1);

  return (
    <div className="host-patterns" data-testid="host-patterns-view">
      <div className="host-patterns-grid">
        {/* Stat cards */}
        <div className="host-stat-card">
          <span className="host-stat-num">{data.total_attempts}</span>
          <span className="host-stat-label">Total Attempts</span>
        </div>
        <div className="host-stat-card">
          <span className="host-stat-num">{f.submitted || 0}</span>
          <span className="host-stat-label">Completed</span>
        </div>
        <div className="host-stat-card">
          <span className="host-stat-num">{t.median_total_minutes != null ? `${t.median_total_minutes}m` : '—'}</span>
          <span className="host-stat-label">Median Duration</span>
        </div>
        <div className="host-stat-card">
          <span className="host-stat-num">{sat.Yes + sat.Mostly + sat.No || 0}</span>
          <span className="host-stat-label">Satisfaction Responses</span>
        </div>
      </div>

      {/* Funnel */}
      <div className="host-section-card">
        <h3>Completion Funnel</h3>
        <div className="host-funnel">
          {funnelSteps.map(([label, val], i) => (
            <div key={i} className="host-funnel-row">
              <span className="host-funnel-label">{label}</span>
              <div className="host-funnel-bar-wrap">
                <div className="host-funnel-bar" style={{ width: `${((val || 0) / maxFunnel) * 100}%` }} />
              </div>
              <span className="host-funnel-val">{val || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Outcomes */}
      <div className="host-section-card">
        <h3>Pathway Outcomes</h3>
        <div className="host-outcomes-grid">
          <OutcomeBadge label="Founders (A)" count={o.pathway_a} color="#5F5085" />
          <OutcomeBadge label="Employment (B)" count={o.pathway_b} color="#002060" />
          <OutcomeBadge label="Independent" count={o.pathway_c} color="#1E491A" />
          <OutcomeBadge label="Close Call" count={o.close_call} color="#F5A800" />
          <OutcomeBadge label="Insufficient" count={o.insufficient} color="#999" />
          <OutcomeBadge label="Chose Different" count={o.chose_different} color="#cc0000" />
        </div>
      </div>

      {/* Time */}
      <div className="host-section-card">
        <h3>Timing (Median Minutes)</h3>
        <div className="host-kv-grid">
          <KV label="Total" value={t.median_total_minutes ?? '-'} />
          <KV label="Section 1" value={t.median_section_1 ?? '-'} />
          <KV label="Section 2" value={t.median_section_2 ?? '-'} />
          <KV label="Section 3" value={t.median_section_3 ?? '-'} />
          <KV label="Section 4" value={t.median_section_4 ?? '-'} />
        </div>
      </div>

      {/* Finance completion */}
      <div className="host-section-card">
        <h3>Financial Section Completion</h3>
        <p className="host-privacy-note">No financial amounts are shown. Only completion rates.</p>
        <div className="host-kv-grid">
          <KV label="Completed All Parts" value={fin.completed_all} />
          <KV label="Partially Completed" value={fin.partial} />
          <KV label="Skipped Entirely" value={fin.skipped} />
        </div>
        <h4 className="host-sub-h">Part Breakdown</h4>
        <div className="host-kv-grid">
          {Object.entries(fin.parts || {}).map(([k, v]) => (
            <KV key={k} label={k.charAt(0).toUpperCase() + k.slice(1)} value={`${v} filled`} />
          ))}
        </div>
      </div>

      {/* Drop off */}
      {data.drop_off?.length > 0 && (
        <div className="host-section-card">
          <h3>Drop-off Points (Drafts)</h3>
          <div className="host-dropoff">
            {data.drop_off.map(([q, count], i) => (
              <div key={i} className="host-dropoff-row">
                <span className="host-dropoff-q">{q}</span>
                <span className="host-dropoff-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Satisfaction */}
      <div className="host-section-card">
        <h3>Satisfaction</h3>
        <div className="host-kv-grid">
          <KV label="Yes" value={sat.Yes || 0} />
          <KV label="Mostly" value={sat.Mostly || 0} />
          <KV label="No" value={sat.No || 0} />
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   SHARED COMPONENTS
   ====================================================================== */
function KV({ label, value }) {
  return (
    <div className="host-kv">
      <span className="host-kv-label">{label}</span>
      <span className="host-kv-value">{value}</span>
    </div>
  );
}

function OutcomeBadge({ label, count, color }) {
  return (
    <div className="host-outcome-badge" style={{ borderColor: color }}>
      <span className="host-outcome-num" style={{ color }}>{count || 0}</span>
      <span className="host-outcome-label">{label}</span>
    </div>
  );
}

/* ======================================================================
   MAIN HOST DASHBOARD
   ====================================================================== */
export default function HostDashboard() {
  const [hostKey, setHostKey] = useState(sessionStorage.getItem('spring_host_key'));
  const [hostName, setHostName] = useState(sessionStorage.getItem('spring_host_name') || '');
  const [view, setView] = useState('people'); // people | person | patterns
  const [selectedPerson, setSelectedPerson] = useState(null);

  const handleLogin = (key, name) => {
    sessionStorage.setItem('spring_host_key', key);
    sessionStorage.setItem('spring_host_name', name);
    setHostKey(key);
    setHostName(name);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('spring_host_key');
    sessionStorage.removeItem('spring_host_name');
    setHostKey(null);
    setHostName('');
  };

  if (!hostKey) return <HostLogin onLogin={handleLogin} />;

  const selectPerson = (id) => { setSelectedPerson(id); setView('person'); };
  const backToPeople = () => { setSelectedPerson(null); setView('people'); };

  return (
    <div className="host-dashboard" data-testid="host-dashboard">
      <div className="host-top-bar">
        <div className="host-top-left">
          <h1 className="host-title">Host Dashboard</h1>
          <span className="host-greeting">Hello, {hostName}</span>
        </div>
        <div className="host-top-nav">
          <button className={`host-nav-btn ${view === 'people' || view === 'person' ? 'active' : ''}`}
            onClick={() => { setView('people'); setSelectedPerson(null); }} data-testid="host-nav-people">
            <Users size={16} /> People
          </button>
          <button className={`host-nav-btn ${view === 'patterns' ? 'active' : ''}`}
            onClick={() => setView('patterns')} data-testid="host-nav-patterns">
            <BarChart3 size={16} /> Patterns
          </button>
        </div>
        <button className="host-btn-outline host-logout" onClick={handleLogout} data-testid="host-logout-btn">
          Log out
        </button>
      </div>

      <div className="host-content">
        {view === 'people' && <PeopleView hostKey={hostKey} onSelectPerson={selectPerson} />}
        {view === 'person' && selectedPerson && <PersonView hostKey={hostKey} attemptId={selectedPerson} onBack={backToPeople} />}
        {view === 'patterns' && <PatternsView hostKey={hostKey} />}
      </div>
    </div>
  );
}
